import { apiClient } from '../lib/apiClient';
import type { Confidence, GridCell, LightGroup, Zone } from './lightSurveyService';
import type { Aquarium } from '../types/aquarium';

/**
 * Flow surveys (flow module "Strömungskarte", docs/flow-map-concept.md phase 1):
 * pumps with their programme, flow readings as ranges on the 0–4 visual scale
 * and corals placed on a tank photo, plus the placement check the API computes
 * on every read (src/services/flow/flowMap.ts). Positions are 0–1 of the photo.
 */
export const FLOW_LEVELS = [0, 1, 2, 3, 4] as const;
export type FlowLevel = (typeof FLOW_LEVELS)[number];

/** visual and video readings are levels; particle and meter readings are cm/s. */
export const FLOW_METHODS = ['visual', 'particle', 'meter', 'video'] as const;
export type FlowMethod = (typeof FLOW_METHODS)[number];

export const PUMP_MODES = ['constant', 'pulse', 'random', 'wave', 'tidal', 'night'] as const;
export type PumpMode = (typeof PUMP_MODES)[number];

/** Directions a pump can point on the photo, degrees clockwise from "right". */
export const PUMP_DIRECTIONS = [0, 45, 90, 135, 180, 225, 270, 315] as const;
/** Out of the photo's plane: towards the front glass (the viewer) or the back glass. */
export const PUMP_FACINGS = ['front', 'back'] as const;
export type PumpFacing = (typeof PUMP_FACINGS)[number];
/** One outlet of a pump: a direction on the photo (degrees) or a facing. Some pumps have two. */
export type PumpOutlet = number | PumpFacing;
/** Every outlet the pump form offers, in its order. */
export const PUMP_OUTLETS: readonly PumpOutlet[] = [...PUMP_DIRECTIONS, ...PUMP_FACINGS];

export type FlowStatus = 'ok' | 'too_weak' | 'too_strong' | 'uncertain' | 'unknown_group' | 'no_data';
export type SpotFlag = 'dead_spot' | 'blasting' | 'laminar' | null;
export type FlowView = 'peak' | 'trough';

export interface FlowSurvey {
  readonly id: string;
  readonly aquariumId: string;
  readonly title: string;
  readonly measuredAt: string;
  /** Local clock time "HH:MM" of the measurement; null when not given. */
  readonly measuredClock: string | null;
  /** The survey this one was copied from (a re-measurement, e.g. at night). */
  readonly linkedSurveyId: string | null;
  readonly imageId: string | null;
  readonly imageUrl: string | null;
  readonly imageWidth: number | null;
  readonly imageHeight: number | null;
  readonly notes: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface FlowSurveySummary extends FlowSurvey {
  readonly pumpCount: number;
  readonly pointCount: number;
  readonly coralCount: number;
}

export interface FlowPump {
  readonly id: string;
  readonly sequence: number;
  readonly label: string;
  readonly x: number;
  readonly y: number;
  /** Where the pump blows: one outlet, or two for pumps that have two. */
  readonly outlets: readonly PumpOutlet[];
  readonly nominalFlowLph: number | null;
  readonly mode: PumpMode;
  readonly minPercent: number;
  readonly maxPercent: number;
  readonly cycleSeconds: number | null;
}

export interface FlowPoint {
  readonly id: string;
  readonly sequence: number;
  readonly x: number;
  readonly y: number;
  readonly zone: Zone | null;
  readonly method: FlowMethod;
  readonly levelMin: FlowLevel;
  readonly levelMax: FlowLevel;
  readonly speedMinCms: number | null;
  readonly speedMaxCms: number | null;
  readonly alternating: boolean;
}

export interface FlowCoral {
  readonly id: string;
  readonly livestockId: string | null;
  readonly livestockName: string | null;
  readonly scientificName: string | null;
  readonly label: string | null;
  readonly careGroup: LightGroup | null;
  readonly group: LightGroup | null;
  readonly x: number;
  readonly y: number;
  readonly zone: Zone | null;
}

export interface FlowCoralAssessment {
  readonly coralId: string;
  readonly group: LightGroup | null;
  readonly peakMin: number | null;
  readonly peakMax: number | null;
  readonly troughMax: number | null;
  readonly status: FlowStatus;
  readonly estimatedMin: number | null;
  readonly estimatedMax: number | null;
  readonly alternating: boolean | null;
  readonly confidence: Confidence | null;
  readonly nearestPointId: string | null;
  readonly suggestionPointId: string | null;
}

export interface Turnover {
  readonly volumeLiters: number | null;
  readonly peakFlowLph: number;
  readonly troughFlowLph: number;
  readonly peak: number | null;
  readonly trough: number | null;
  readonly pumpsWithoutFlow: number;
  readonly targets: { readonly softLps: readonly [number, number]; readonly sps: readonly [number, number] };
}

export interface FlowTimeSlot {
  readonly start: string;
  readonly end: string;
  readonly measured: boolean;
  readonly deadSpotCount: number;
  readonly blastingCount: number;
  readonly corals: ReadonlyArray<{ readonly coralId: string; readonly status: FlowStatus }>;
}

export interface FlowAssessment {
  /** The time the values are shown for: null = as measured; else estimated from the pump schedules. */
  readonly at: string | null;
  readonly estimated: boolean;
  /** The ranges used per reading (the measured ones unless estimated). */
  readonly levels: ReadonlyArray<{ readonly id: string; readonly levelMin: FlowLevel; readonly levelMax: FlowLevel }>;
  readonly timeline: readonly FlowTimeSlot[];
  readonly timeDrops: ReadonlyArray<{ readonly coralId: string; readonly slots: readonly string[] }>;
  readonly scheduledPumpIds: readonly string[];
  readonly points: Array<{ readonly id: string; readonly flag: SpotFlag }>;
  readonly corals: FlowCoralAssessment[];
  readonly spots: Array<{ readonly group: LightGroup; readonly pointIds: string[] }>;
  /** Cells whose `par` holds the estimated level (0–4). */
  readonly grid: { readonly peak: GridCell[]; readonly trough: GridCell[] };
  readonly turnover: Turnover;
  readonly deadSpotCount: number;
  readonly blastingCount: number;
  readonly source: string;
}

export interface LinkedFlowSurvey {
  readonly id: string;
  readonly title: string;
  readonly measuredAt: string;
  readonly measuredClock: string | null;
}

export interface FlowSurveyDetail extends FlowSurvey {
  readonly linkedSurveys: readonly LinkedFlowSurvey[];
  readonly pumps: FlowPump[];
  readonly points: FlowPoint[];
  readonly corals: FlowCoral[];
  readonly assessment: FlowAssessment;
}

export interface FlowSurveyInput {
  title?: string;
  measured_at?: string;
  measured_clock?: string | null;
  image_id?: string | null;
  notes?: string | null;
}

export interface PumpInput {
  label?: string;
  x?: number;
  y?: number;
  outlets?: PumpOutlet[];
  nominal_flow_lph?: number | null;
  mode?: PumpMode;
  min_percent?: number;
  max_percent?: number;
  cycle_seconds?: number | null;
}

/** A pump not in the aquarium's equipment yet: the API adds it there and "Brand Model" becomes the label. */
export interface EquipmentPumpUnitInput {
  type: 'flow_pump' | 'return_pump';
  brand: string;
  model: string;
}

/** A pump picked from the equipment (label) or a new one (equipment) — one of the two. */
export type NewPumpInput = Required<Pick<PumpInput, 'x' | 'y' | 'outlets' | 'mode' | 'min_percent' | 'max_percent'>> &
  Omit<PumpInput, 'label'> &
  ({ label: string; equipment?: never } | { equipment: EquipmentPumpUnitInput; label?: never });

/** A reading: visual levels, or particle-test / meter speeds in cm/s (the API derives the levels). */
export type ReadingInput =
  | { method: 'visual' | 'video'; level_min: number; level_max: number }
  | { method: 'particle' | 'meter'; speed_min: number; speed_max: number };

export type FlowPointInput = { x: number; y: number; zone?: Zone | null; alternating?: boolean } & ReadingInput;

export interface FlowCoralInput {
  x?: number;
  y?: number;
  zone?: Zone | null;
  livestock_id?: string | null;
  label?: string | null;
  care_group?: LightGroup | null;
}

/** A pump from the aquarium's equipment (Aquarium → Equipment: flow and return pumps). */
export interface EquipmentPumpOption {
  /** Stable within the list, e.g. "flow_pump:0". */
  readonly key: string;
  /** "Brand Model" — stored as the pump's label. */
  readonly name: string;
  readonly type: 'flow_pump' | 'return_pump';
}

/** The flow and return pumps from the aquarium's equipment ("Brand Model"), for the pump editor. */
export function equipmentPumpOptions(aquarium: Pick<Aquarium, 'equipment'> | undefined): EquipmentPumpOption[] {
  return (aquarium?.equipment ?? []).flatMap((item): EquipmentPumpOption[] => {
    if (item.type !== 'flow_pump' && item.type !== 'return_pump') return [];
    const type: EquipmentPumpOption['type'] = item.type;
    return item.units
      .map((u, i) => ({ key: `${type}:${i}`, name: `${u.brand ?? ''} ${u.model ?? ''}`.trim().slice(0, 120), type }))
      .filter((p) => p.name !== '');
  });
}

const base = (aquariumId: string) => `/api/v1/aquariums/${aquariumId}/flow-surveys`;
const item = (aquariumId: string, surveyId: string, kind?: 'pumps' | 'points' | 'corals', id?: string): string =>
  `${base(aquariumId)}/${encodeURIComponent(surveyId)}${kind ? `/${kind}` : ''}${id ? `/${encodeURIComponent(id)}` : ''}`;

export const flowSurveysKey = (aquariumId: string) => ['flow-surveys', aquariumId] as const;
export const flowSurveyKey = (aquariumId: string, surveyId: string) => ['flow-surveys', aquariumId, surveyId] as const;
export const flowSchedulesKey = (aquariumId: string) => ['flow-schedules', aquariumId] as const;

/** Lower bounds (cm/s) of levels 1–4 — the same as the API's levelForSpeed. */
export const LEVEL_SPEED_BOUNDS = [2, 5, 15, 30] as const;

export function levelForSpeed(cmPerSecond: number): FlowLevel {
  let level = 0;
  for (const bound of LEVEL_SPEED_BOUNDS) if (cmPerSecond >= bound) level++;
  return level as FlowLevel;
}

/** Particle test: distance (cm) over time (s) → cm/s, one decimal; null for zero or negative input. */
export function particleSpeed(distanceCm: number, seconds: number): number | null {
  if (!(seconds > 0) || !(distanceCm > 0)) return null;
  return Math.round((distanceCm / seconds) * 10) / 10;
}

/** "1–3" or "2" when both ends are equal. */
export function formatRange(min: number, max: number): string {
  return min === max ? String(min) : `${min}–${max}`;
}

export async function listFlowSurveys(aquariumId: string): Promise<FlowSurveySummary[]> {
  const res = await apiClient.get<{ data: FlowSurveySummary[] }>(base(aquariumId));
  return Array.isArray(res?.data) ? res.data : [];
}

/** A survey — with `at` ("HH:MM") the values estimated for that time of day from the pump schedules. */
export async function getFlowSurvey(aquariumId: string, surveyId: string, at?: string): Promise<FlowSurveyDetail> {
  const query = at ? `?at=${encodeURIComponent(at)}` : '';
  const res = await apiClient.get<{ data: FlowSurveyDetail }>(`${item(aquariumId, surveyId)}${query}`);
  return res.data;
}

/** Measure again (e.g. at night): a linked copy with the pumps, readings and corals of the survey. */
export async function copyFlowSurvey(aquariumId: string, surveyId: string, input: { title: string; measured_at?: string; measured_clock?: string | null }): Promise<FlowSurveyDetail> {
  const res = await apiClient.post<{ data: FlowSurveyDetail }>(`${item(aquariumId, surveyId)}/copy`, input);
  return res.data;
}

// ─── Pump day schedules (phase 3) ────────────────────────────────────────────

export interface ScheduleSegment {
  readonly start: string;
  readonly end: string;
  readonly mode: PumpMode;
  readonly minPercent: number;
  readonly maxPercent: number;
}

export interface FlowSchedule {
  readonly id: string;
  readonly pumpLabel: string;
  readonly segments: readonly ScheduleSegment[];
  readonly updatedAt: string;
}

const schedules = (aquariumId: string) => `/api/v1/aquariums/${aquariumId}/flow-schedules`;

export async function listFlowSchedules(aquariumId: string): Promise<FlowSchedule[]> {
  const res = await apiClient.get<{ data: FlowSchedule[] }>(schedules(aquariumId));
  return Array.isArray(res?.data) ? res.data : [];
}

/** Creates or replaces the day schedule of a pump (matched by name). */
export async function saveFlowSchedule(aquariumId: string, pumpLabel: string, segments: readonly ScheduleSegment[]): Promise<FlowSchedule> {
  const res = await apiClient.put<{ data: FlowSchedule }>(schedules(aquariumId), {
    pump_label: pumpLabel,
    segments: segments.map((s) => ({ start: s.start, end: s.end, mode: s.mode, min_percent: s.minPercent, max_percent: s.maxPercent })),
  });
  return res.data;
}

export async function deleteFlowSchedule(aquariumId: string, scheduleId: string): Promise<void> {
  await apiClient.delete(`${schedules(aquariumId)}/${encodeURIComponent(scheduleId)}`);
}

/** "HH:MM" from minutes after midnight (wraps). */
export function clockOf(minutes: number): string {
  const m = ((Math.round(minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

export function minutesOf(clock: string): number {
  const [h, m] = clock.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

export async function createFlowSurvey(aquariumId: string, input: FlowSurveyInput & { title: string }): Promise<FlowSurveyDetail> {
  const res = await apiClient.post<{ data: FlowSurveyDetail }>(base(aquariumId), input);
  return res.data;
}

export async function updateFlowSurvey(aquariumId: string, surveyId: string, input: FlowSurveyInput): Promise<FlowSurveyDetail> {
  const res = await apiClient.patch<{ data: FlowSurveyDetail }>(item(aquariumId, surveyId), input);
  return res.data;
}

export async function deleteFlowSurvey(aquariumId: string, surveyId: string): Promise<void> {
  await apiClient.delete(item(aquariumId, surveyId));
}

export async function addPump(aquariumId: string, surveyId: string, input: NewPumpInput): Promise<FlowSurveyDetail> {
  const res = await apiClient.post<{ data: FlowSurveyDetail }>(item(aquariumId, surveyId, 'pumps'), input);
  return res.data;
}

export async function updatePump(aquariumId: string, surveyId: string, pumpId: string, input: PumpInput | NewPumpInput): Promise<FlowSurveyDetail> {
  const res = await apiClient.patch<{ data: FlowSurveyDetail }>(item(aquariumId, surveyId, 'pumps', pumpId), input);
  return res.data;
}

export async function addFlowPoint(aquariumId: string, surveyId: string, input: FlowPointInput): Promise<FlowSurveyDetail> {
  const res = await apiClient.post<{ data: FlowSurveyDetail }>(item(aquariumId, surveyId, 'points'), input);
  return res.data;
}

export async function updateFlowPoint(aquariumId: string, surveyId: string, pointId: string, input: Partial<FlowPointInput>): Promise<FlowSurveyDetail> {
  const res = await apiClient.patch<{ data: FlowSurveyDetail }>(item(aquariumId, surveyId, 'points', pointId), input);
  return res.data;
}

export async function addFlowCoral(aquariumId: string, surveyId: string, input: Required<Pick<FlowCoralInput, 'x' | 'y'>> & FlowCoralInput): Promise<FlowSurveyDetail> {
  const res = await apiClient.post<{ data: FlowSurveyDetail }>(item(aquariumId, surveyId, 'corals'), input);
  return res.data;
}

export async function updateFlowCoral(aquariumId: string, surveyId: string, coralId: string, input: FlowCoralInput): Promise<FlowSurveyDetail> {
  const res = await apiClient.patch<{ data: FlowSurveyDetail }>(item(aquariumId, surveyId, 'corals', coralId), input);
  return res.data;
}

/** Removes a pump, reading or coral; answers with the recomputed survey. */
export async function deleteFlowItem(aquariumId: string, surveyId: string, kind: 'pumps' | 'points' | 'corals', id: string): Promise<FlowSurveyDetail> {
  const res = await apiClient.delete<{ data: FlowSurveyDetail }>(item(aquariumId, surveyId, kind, id));
  return res.data;
}

/** The overlay picture as a large JPEG: GET …/overlay.jpg (`estimate` false leaves out the level map). */
export async function downloadFlowOverlay(
  aquariumId: string,
  survey: Pick<FlowSurvey, 'id' | 'title' | 'measuredAt'>,
  options: { estimate: boolean; view: FlowView },
): Promise<{ blob: Blob; filename: string }> {
  const { blob, filename } = await apiClient.getBlob(`${item(aquariumId, survey.id)}/overlay.jpg`, {
    params: { estimate: options.estimate ? undefined : '0', view: options.view === 'trough' ? 'trough' : undefined },
  });
  const slug = survey.title.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'survey';
  return { blob, filename: filename ?? `flow-map-${slug}-${survey.measuredAt.slice(0, 10)}.jpg` };
}

// ─── Flow evaluation agent (AI) ──────────────────────────────────────────────
// src/services/flow/flowEvaluationService.ts: the overlays at peak and trough, the photo,
// the readings, pumps and programmes (and the light map's zones) judged by the AI.

/** A survey needs this many readings before the AI can evaluate it (MIN_READINGS_FOR_EVALUATION). */
export const MIN_READINGS_FOR_EVALUATION = 3;

export type FlowVerdict = 'adequate' | 'dead_spots' | 'too_strong' | 'uneven' | 'insufficient_data';
export type FlowZoneAssessment = 'too_weak' | 'adequate' | 'too_strong';
export type FlowPlacement = 'well_placed' | 'needs_more_flow' | 'needs_less_flow' | 'needs_variable_flow' | 'uncertain';
export type FlowCondition = 'healthy' | 'polyps_extended' | 'polyps_closed' | 'tissue_pressed_or_damaged' | 'detritus_or_algae' | 'not_visible';
export type FlowAction = 'redirect_pump' | 'change_programme' | 'change_night_mode' | 'add_pump' | 'move_coral' | 'measure_more' | 'other';
export type FlowPriority = 'high' | 'medium' | 'low';
export type FlowBand = 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom';

/** src/services/flow/flowZones.ts: computed flow zones (not AI). */
export interface FlowZoneStats {
  readonly band: FlowBand;
  readonly pointLabels: readonly string[];
  readonly medianMin: number;
  readonly medianMax: number;
  readonly deadSpotLabels: readonly string[];
  readonly blastingLabels: readonly string[];
  readonly alternatingShare: number;
  readonly suits: readonly LightGroup[];
}

export interface FlowZones {
  readonly horizontal: readonly FlowZoneStats[];
  readonly levels: readonly FlowZoneStats[];
  readonly deadSpotLabels: readonly string[];
  readonly blastingLabels: readonly string[];
  readonly laminarLabels: readonly string[];
}

/** Light and flow per zone, from the aquarium's newest light map. */
export interface CombinedZone {
  readonly band: FlowBand;
  readonly medianPar: number;
  readonly flowRange: readonly [number, number];
  readonly lightSuits: readonly LightGroup[];
  readonly flowSuits: readonly LightGroup[];
  readonly suits: readonly LightGroup[];
}

export interface FlowEvaluationResult {
  readonly verdict: FlowVerdict;
  readonly summary: string;
  readonly confidence: Confidence;
  readonly zones: ReadonlyArray<{
    readonly zone: string;
    readonly assessment: FlowZoneAssessment;
    readonly suitableGroups: readonly LightGroup[];
    readonly pointLabels: readonly string[];
    readonly detail: string;
  }>;
  readonly corals: ReadonlyArray<{
    readonly coralNumber: number;
    readonly coralId: string;
    readonly name: string;
    readonly placement: FlowPlacement;
    readonly condition: FlowCondition;
    readonly comment: string;
  }>;
  readonly recommendations: ReadonlyArray<{ readonly action: FlowAction; readonly priority: FlowPriority; readonly pumpNumber: number | null; readonly text: string }>;
  readonly addPumpWithheld: boolean;
  readonly flowZones: FlowZones | null;
  readonly combinedZones: readonly CombinedZone[];
  readonly usedPhoto: boolean;
  readonly usedOverlay: boolean;
  readonly usedLightMap: boolean;
  readonly pointCount: number;
  readonly coralCount: number;
  readonly pumpCount: number;
}

export interface FlowEvaluation {
  readonly id: string;
  readonly surveyId: string;
  readonly aquariumId: string;
  readonly surveyTitle: string;
  readonly surveyMeasuredAt: string;
  readonly language: string;
  readonly provider: string;
  readonly model: string;
  readonly createdAt: string;
  /** The survey or the light map changed after this evaluation. */
  readonly outdated: boolean;
  readonly result: FlowEvaluationResult;
}

export const flowEvaluationKey = (aquariumId: string, surveyId: string) => ['flow-surveys', aquariumId, surveyId, 'evaluation'] as const;
export const latestFlowEvaluationKey = (aquariumId: string) => ['flow-surveys', aquariumId, 'evaluations', 'latest'] as const;

export async function getFlowEvaluation(aquariumId: string, surveyId: string): Promise<FlowEvaluation | null> {
  const res = await apiClient.get<{ data: FlowEvaluation | null }>(`${item(aquariumId, surveyId)}/evaluation`);
  return res?.data ?? null;
}

export async function getLatestFlowEvaluation(aquariumId: string): Promise<FlowEvaluation | null> {
  const res = await apiClient.get<{ data: FlowEvaluation | null }>(`${base(aquariumId)}/evaluations/latest`);
  return res?.data ?? null;
}

/**
 * Runs the flow evaluation agent; the stored one comes back for an unchanged survey unless
 * `force`. null = still being generated (202): it is stored when done, poll getFlowEvaluation.
 */
export async function evaluateFlowSurvey(aquariumId: string, surveyId: string, force = false): Promise<FlowEvaluation | null> {
  const res = await apiClient.post<{ data: FlowEvaluation | null }>(`${item(aquariumId, surveyId)}/evaluation`, force ? { force: true } : {});
  return res.data ?? null;
}

// ─── Video estimate (phase 5) ────────────────────────────────────────────────

export interface VideoEstimate {
  /** false: nothing that shows the flow could be seen; no levels. */
  readonly visible: boolean;
  readonly levelMin: FlowLevel | null;
  readonly levelMax: FlowLevel | null;
  readonly alternating: boolean | null;
  readonly confidence: Confidence;
  readonly explanation: string;
  readonly frameCount: number;
  readonly language: string;
}

/**
 * The AI's level range from still frames of a short clip (the clip itself is
 * never uploaded): POST …/video-estimate as multipart. Nothing is stored — the
 * result fills the reading form.
 */
export async function estimateFlowFromVideo(
  aquariumId: string,
  surveyId: string,
  frames: readonly Blob[],
  meta: { durationSeconds: number | null; x: number | null; y: number | null },
): Promise<VideoEstimate> {
  const form = new FormData();
  frames.forEach((f, i) => form.append('frames', f, `frame-${i + 1}.jpg`));
  if (meta.durationSeconds !== null) form.append('duration', String(Math.round(meta.durationSeconds)));
  if (meta.x !== null) form.append('x', String(meta.x));
  if (meta.y !== null) form.append('y', String(meta.y));
  const res = await apiClient.post<{ data: VideoEstimate }>(`${item(aquariumId, surveyId)}/video-estimate`, form);
  return res.data;
}
